import React from 'react';
import { TrendingUp, TrendingDown, Minus, Zap, Globe, Activity } from 'lucide-react';

// --- Types ---
export interface TickerItem {
  symbol: string;
  brokerage: string;
  recommendation: 'Buy' | 'Sell' | 'Hold' | 'Strong Buy' | 'Strong Sell';
  upside: number;
  price?: number; // Added for realism
}

interface LiveTickerProps {
  items?: TickerItem[];
}

// --- Mock Data for "Real" Feel (Fallback) ---
const MOCK_ITEMS: TickerItem[] = [
  { symbol: 'RELIANCE', brokerage: 'Morgan Stanley', recommendation: 'Buy', upside: 12.5, price: 2450.00 },
  { symbol: 'TCS', brokerage: 'JP Morgan', recommendation: 'Hold', upside: 2.1, price: 3400.50 },
  { symbol: 'HDFCBANK', brokerage: 'Goldman Sachs', recommendation: 'Strong Buy', upside: 18.4, price: 1650.00 },
  { symbol: 'INFY', brokerage: 'Citi', recommendation: 'Sell', upside: -5.2, price: 1420.00 },
  { symbol: 'ICICIBANK', brokerage: 'CLSA', recommendation: 'Buy', upside: 15.0, price: 980.00 },
  { symbol: 'SBIN', brokerage: 'Jefferies', recommendation: 'Buy', upside: 22.0, price: 620.00 },
  { symbol: 'ITC', brokerage: 'Nomura', recommendation: 'Hold', upside: 1.5, price: 445.00 },
  { symbol: 'BAJFINANCE', brokerage: 'HSBC', recommendation: 'Sell', upside: -8.5, price: 7100.00 },
];

export const LiveTicker: React.FC<LiveTickerProps> = ({ items = [] }) => {
  // Use mock items if no props provided (for preview)
  const displayItems = items.length > 0 ? items : MOCK_ITEMS;
  
  // Duplicate items to create the seamless infinite loop
  const tickerContent = [...displayItems, ...displayItems];

  const getRecommendationColor = (rec: string) => {
    const r = rec.toLowerCase();
    if (r.includes('buy')) {
      return {
        bg: 'bg-emerald-500/5',
        border: 'border-emerald-500/20',
        text: 'text-emerald-400',
        icon: 'text-emerald-500',
        indicator: 'bg-emerald-500'
      };
    }
    if (r.includes('sell')) {
      return {
        bg: 'bg-red-500/5',
        border: 'border-red-500/20',
        text: 'text-red-400',
        icon: 'text-red-500',
        indicator: 'bg-red-500'
      };
    }
    return {
      bg: 'bg-yellow-500/5',
      border: 'border-yellow-500/20',
      text: 'text-yellow-400',
      icon: 'text-yellow-500',
      indicator: 'bg-yellow-500'
    };
  };

  return (
    <div className="w-full bg-[#05070A] border-y border-white/5 relative z-30 font-sans">
      
      {/* 1. Header Bar (Terminal Style) */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#0A0C10] border-b border-white/5 text-[10px] uppercase tracking-widest font-bold text-gray-500">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-emerald-500">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="shadow-emerald-500/50 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]">Live Market Feed</span>
          </div>
          <div className="h-3 w-px bg-white/10 hidden sm:block"></div>
          <div className="hidden sm:flex items-center gap-2 text-gray-400">
            <Globe className="w-3 h-3" />
            <span>NSE/BSE Realtime</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
            <Activity className="w-3 h-3 text-blue-500" />
            <span>Updates: 24ms</span>
        </div>
      </div>

      {/* 2. Infinite Ticker Container */}
      <div className="relative overflow-hidden py-3 group">
        
        {/* Gradient Fades for depth */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#05070A] to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#05070A] to-transparent z-10 pointer-events-none"></div>

        {/* Moving Track */}
        <div className="flex w-max animate-marquee group-hover:[animation-play-state:paused]">
          {tickerContent.map((item, idx) => {
            const style = getRecommendationColor(item.recommendation);
            const isPositive = item.upside > 0;
            
            return (
              <div 
                key={`${item.symbol}-${idx}`}
                className={`
                  flex-shrink-0 mx-2 px-4 py-2 rounded-lg border backdrop-blur-sm transition-colors duration-300
                  ${style.bg} ${style.border} hover:bg-white/5 hover:border-white/10
                  flex items-center gap-4 min-w-[280px]
                `}
              >
                {/* Symbol & Price */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-gray-100">{item.symbol}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${style.bg} ${style.text} border border-white/5`}>
                      {item.recommendation}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs mt-0.5">
                    <span className="text-gray-500">{item.brokerage}</span>
                    {item.price && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-gray-700"></span>
                        <span className="text-gray-300 font-mono">₹{item.price.toLocaleString()}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Upside / Downside */}
                <div className="flex-1 text-right">
                  <div className={`flex items-center justify-end gap-1 font-bold text-sm ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span>{isPositive ? '+' : ''}{item.upside.toFixed(1)}%</span>
                  </div>
                  <div className="text-[10px] text-gray-600 font-medium uppercase tracking-wider mt-0.5">
                    Target Upside
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Global Style for the animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 80s linear infinite;
        }
      `}</style>
    </div>
  );
};